// ============================================================================
// Activity visual tokens (ORG-USAGE-2026)
//
// Severity is ORDINAL (how close to a limit), not identity, so it uses a
// 3-step status scale rather than a categorical palette. "Normal" is a
// recessive neutral on purpose — a member who is nowhere near a limit should
// see no alarm color at all. Every severity state is ALWAYS accompanied by a
// text label and a percentage, so status is never conveyed by color alone.
//
// Separation verified with the dataviz palette validator (light + dark):
// adjacent-pair ΔE clears the normal-vision floor and CVD separation.
// ============================================================================

import { usdToWu } from '../../config/workUnits';

export const ACTIVITY_COLORS = {
  /** magnitude bars — single series, single hue (brand gold) */
  magnitude: '#C6A75E',
  magnitudeDark: '#D9BE7E',

  severity: {
    normal: '#6b6b66',
    approaching: '#B8860B',
    reached: '#991B1B',
  },
  severityDark: {
    normal: '#a3a39c',
    approaching: '#E0A93B',
    reached: '#E06C6C',
  },

  surface: '#ffffff',
  border: '#e6e6e6',
  gridline: '#f0f0ee',
  ink: '#1a1a1a',
  inkSecondary: '#666666',
  inkMuted: '#999999',
} as const;

/**
 * Categorical series hues for the by-model stack — the validated reference
 * palette (dataviz method; six-checks validator run 2026-08-03: lightness
 * band, chroma floor, CVD separation, and normal-vision floor all PASS on the
 * light surface; the contrast WARN's relief obligation is met by the legend
 * labels and the ranked by-model table on the same page). Assigned in FIXED
 * rank order and never cycled; the overflow bucket gets the neutral.
 */
export const CATEGORICAL_SERIES = [
  '#2a78d6', // blue
  '#eb6834', // orange
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#e87ba4', // magenta
  '#4a3aa7', // violet
] as const;

export const OTHER_SERIES_COLOR = '#8a8a83';

/**
 * Mix a hex color toward white (t > 0) or black (t < 0), t in [-1, 1].
 * Used to give same-family systems distinct shades of their family color —
 * color follows the FAMILY (matching the provider dots on the events table
 * and the ⓘ popup), shade separates siblings.
 */
export function mixHex(hex: string, t: number): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  if (!Number.isFinite(n)) return hex;
  const target = t >= 0 ? 255 : 0;
  const k = Math.min(1, Math.abs(t));
  const mix = (c: number) => Math.round(c + (target - c) * k);
  const r = mix((n >> 16) & 255);
  const g = mix((n >> 8) & 255);
  const b = mix(n & 255);
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/** Shade ladder for the 2nd, 3rd… system within one family (1st = base).
    R20 (2026-08-19 Miles, "some colors don't show up"): steps widened — the
    old .34/.55 and -.28/-.45 pairs were near-indistinguishable at sliver
    size; every adjacent rung now differs by ≥0.18 mix. */
export const FAMILY_SHADE_STEPS = [0, 0.42, -0.34, 0.62, -0.52, 0.78];

/**
 * R20: how many bars the LIVE default view focuses on — sized so each bar
 * renders at a comfortably readable width (~20px) in the current viewport.
 * The full selected range stays one click away; custom/zoom windows are
 * never focused.
 */
export const FOCUS_TARGET_BAR_PX = 20;
export function optimalFocusBars(viewportWidth?: number): number {
  if (!viewportWidth || !Number.isFinite(viewportWidth)) return 48;
  const plotWidth = Math.max(240, Math.min(1650, viewportWidth - 220));
  return Math.max(12, Math.min(96, Math.floor(plotWidth / FOCUS_TARGET_BAR_PX)));
}

export function formatDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds >= 10 ? Math.round(seconds) : seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const rem = Math.round(seconds % 60);
  return `${minutes}m ${rem}s`;
}

export type SeverityLevel = 'none' | 'approaching' | 'critical' | 'reached';

export function severityColor(level: SeverityLevel): string {
  switch (level) {
    case 'approaching':
      return ACTIVITY_COLORS.severity.approaching;
    case 'critical':
    case 'reached':
      return ACTIVITY_COLORS.severity.reached;
    default:
      return ACTIVITY_COLORS.severity.normal;
  }
}

/** Text label that accompanies every severity color (never color alone). */
export function severityLabel(level: SeverityLevel): string {
  switch (level) {
    case 'approaching':
      return 'approaching limit';
    case 'critical':
      return 'nearly at limit';
    case 'reached':
      return 'limit reached';
    default:
      return 'within limit';
  }
}

// ── PRO Work Units (2026-08-13 presentation migration) ──────────────────────
// Activity display sites render WU, never raw token counts. WU is derived
// CLIENT-SIDE from the user-charged USD the backend already returns (no
// contract change): WU = amountUsd / STANDARD_RATE_USD_PER_WU. Both the
// events table and the chat ⓘ popup derive from the same USD, so the two
// surfaces reconcile (R18 spirit).
export { formatWu, formatWuDetail, formatWuWithUnit, WU_LABEL } from '../../config/workUnits';

/** Display-derived Work Units from a user-charged USD amount. */
export function wuFromUsd(usd: number): number {
  return usdToWu(Number(usd) || 0);
}

// Kept for non-Activity importers; Activity display sites no longer call it.
export function formatTokens(n: number): string {
  const v = Math.round(Number(n) || 0);
  if (v >= 1_000_000) {
    const m = v / 1_000_000;
    return `${m >= 10 ? Math.round(m) : m.toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (v >= 1_000) {
    const k = v / 1_000;
    return `${k >= 10 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, '')}K`;
  }
  return String(v);
}

export function formatUsd(n: number): string {
  const v = Number(n) || 0;
  if (v > 0 && v < 0.01) return '<$0.01';
  return `$${v.toFixed(2)}`;
}

export function formatResetTime(resetMs: number, nowMs: number = Date.now()): string {
  const delta = resetMs - nowMs;
  if (delta <= 0) return 'shortly';
  // One rounded quantity for both units — see describeReset in
  // functions/src/lib/limitWindows.ts. Mixing ceil and floor here produced
  // "in 0h" for a reset 59–60 minutes away.
  const totalMinutes = Math.ceil(delta / 60_000);
  if (totalMinutes < 60) return `in ${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  if (hours < 24) return `in ${hours}h`;
  const days = Math.round(delta / 86_400_000);
  return `in ${days} day${days === 1 ? '' : 's'}`;
}

// ── Wallet-consistent card + control chrome (R19.5 style unification) ────────
// The wallet page's visual language is the product's data-page style: warm
// #FAFAF8 page ground, white cards with 1rem radius and a soft wide shadow,
// desktop hover lift, gold-ring focus on inputs. The activity page keeps its
// LAYOUT but wears the same skin, down to the calendar popover.
export const PAGE_GROUND = '#FAFAF8';

export const CARD_STYLE = `
  background: #ffffff;
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 1rem;
  box-shadow: 0 0.375rem 1.25rem rgba(0, 0, 0, 0.04);
`;

export const CARD_HOVER_STYLE = `
  @media (min-width: 1024px) {
    transition: box-shadow 0.3s ease, transform 0.3s ease;
    &:hover {
      box-shadow: 0 0.5rem 1.875rem rgba(0, 0, 0, 0.06);
      transform: translateY(-2px);
    }
  }
`;

export const INPUT_FOCUS_STYLE = `
  &:focus {
    outline: none;
    border-color: ${ACTIVITY_COLORS.magnitude};
    box-shadow: 0 0 0 2px rgba(198, 167, 94, 0.15);
  }
`;

// ── Chart granularity options (R19.5) ────────────────────────────────────────
// Mirrors the backend BUCKET_MAX_SPAN_DAYS caps: the picker only offers
// granularities the current range can honestly render, and the server
// coarsens anything out of bounds anyway.
export type ActivityBucketValue =
  | '1m' | '5m' | '15m' | '30m'
  | '1h' | '3h' | '6h' | '12h'
  | '1d' | '2d' | '1w' | '1mo';

export const BUCKET_OPTIONS: Array<{
  value: ActivityBucketValue;
  label: string;
  maxSpanDays: number;
}> = [
  { value: '1m', label: 'per minute', maxSpanDays: 2 },
  { value: '5m', label: 'per 5 minutes', maxSpanDays: 3 },
  { value: '15m', label: 'per 15 minutes', maxSpanDays: 7 },
  { value: '30m', label: 'per 30 minutes', maxSpanDays: 14 },
  { value: '1h', label: 'per hour', maxSpanDays: 31 },
  { value: '3h', label: 'per 3 hours', maxSpanDays: 62 },
  { value: '6h', label: 'per 6 hours', maxSpanDays: 92 },
  { value: '12h', label: 'per 12 hours', maxSpanDays: 92 },
  { value: '1d', label: 'per day', maxSpanDays: 366 },
  { value: '2d', label: 'per 2 days', maxSpanDays: 366 },
  { value: '1w', label: 'per week', maxSpanDays: 366 },
  { value: '1mo', label: 'per month', maxSpanDays: 366 },
];

export function allowedBucketsForSpan(spanDays: number): ActivityBucketValue[] {
  return BUCKET_OPTIONS.filter((o) => spanDays <= o.maxSpanDays).map((o) => o.value);
}

// ── Auto granularity (round 13) ──────────────────────────────────────────────
// The chart picks its own bucket by default: fine enough that the bars carry
// shape, coarse enough that each bar stays readable. Targets a bar count in
// [AUTO_MIN_BARS, AUTO_MAX_BARS] — the band where bars are neither so fat
// they say nothing nor so skinny the system colors blur (owner, round 13).

export type ActivityBucketChoice = 'auto' | ActivityBucketValue;

/** Fallback bar budget when no viewport estimate is available. */
export const AUTO_MAX_BARS = 72;
/** A bucket needs about this many px to keep its color readable (calibrated
    against the owner's per-hour-over-7-days reference view, ~11px/bar). */
export const AUTO_MIN_BAR_PX = 9;

/**
 * How many bars the plot can comfortably hold at this viewport width
 * (round 15: the budget is PIXEL-driven — a desktop earns per-hour bars on a
 * 7-day horizon where a phone gets per-6-hours, both equally readable).
 * The estimate mirrors the page chrome: container padding, panel padding,
 * y-axis gutter.
 */
export function autoBarBudget(viewportWidth?: number): number {
  if (!viewportWidth || !Number.isFinite(viewportWidth)) return AUTO_MAX_BARS;
  const plotWidth = Math.max(240, Math.min(1650, viewportWidth - 220));
  return Math.max(24, Math.floor(plotWidth / AUTO_MIN_BAR_PX));
}

const BUCKET_WIDTH_MS: Record<Exclude<ActivityBucketValue, '1mo'>, number> = {
  '1m': 60_000,
  '5m': 300_000,
  '15m': 900_000,
  '30m': 1_800_000,
  '1h': 3_600_000,
  '3h': 10_800_000,
  '6h': 21_600_000,
  '12h': 43_200_000,
  '1d': 86_400_000,
  '2d': 172_800_000,
  '1w': 604_800_000,
};

/** Nominal width of one bucket (the '1mo' average is close enough here). */
export function bucketWidthMs(bucket: ActivityBucketValue): number {
  return bucket === '1mo' ? 30 * 86_400_000 : BUCKET_WIDTH_MS[bucket];
}

// ── Timeline display mode (2026-08-17 owner directive) ───────────────────────
// The chart timeline must reflect the SAME recorded activity the events list
// shows. The old empty-state check keyed off the USD series alone, so any
// window whose recorded activity carried no billable spend — $0-billed turns,
// or accounts whose every turn is test mode (owner testing toggle / internal
// test accounts) — claimed "no usage recorded in this period yet" while the
// list right below showed the rows. Three honest states instead:
//   'spend'    → billable spend exists; render the usual spend/WU chart.
//   'activity' → no billable spend, but recorded tasks exist (billable-$0
//                and/or test turns); render task-count bars.
//   'empty'    → genuinely nothing recorded; only THEN show the empty note.

export type TimelineDisplayMode = 'spend' | 'activity' | 'empty';

export interface TimelineSeriesPoint {
  usd: number;
  turns?: number;
  testTurns?: number;
}

export function timelineDisplayMode(
  series: ReadonlyArray<TimelineSeriesPoint>
): TimelineDisplayMode {
  let hasActivity = false;
  for (const p of series) {
    if ((p.usd || 0) > 0) return 'spend';
    if ((p.turns || 0) > 0 || (p.testTurns || 0) > 0) hasActivity = true;
  }
  return hasActivity ? 'activity' : 'empty';
}

/**
 * Clean INTEGER axis ticks from 0 up to (and just covering) max — the
 * task-count timeline must never label a fractional task.
 */
export function integerTicks(max: number, targetCount = 4): number[] {
  const m = Math.max(1, Math.ceil(Number(max) || 0));
  const raw = m / targetCount;
  const pow = 10 ** Math.floor(Math.log10(Math.max(raw, 1)));
  const n = raw / pow;
  const factor = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  const step = Math.max(1, Math.round(factor * pow));
  const top = Math.ceil(m / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  return ticks;
}

/**
 * Choose the cadence for the FULL horizon being plotted (round 15 owner
 * directive: auto keys off the whole timeline, never a data-dependent slice
 * — predictable, and the zoom gesture is the intentional way to go finer).
 * Picks the finest bucket whose bar count fits the pixel-driven budget,
 * never exceeding a bucket's own span cap; a span so short that even the
 * finest bucket underfills just takes the finest allowed (few bars of a
 * tiny window are honest, not a bug).
 */
export function pickAutoBucket(spanMs: number, maxBars: number = AUTO_MAX_BARS): ActivityBucketValue {
  const spanDays = Math.max(spanMs, 60_000) / 86_400_000;
  const allowed = BUCKET_OPTIONS.filter((o) => spanDays <= o.maxSpanDays).map((o) => o.value);
  if (allowed.length === 0) return '1mo';
  let pick: ActivityBucketValue = allowed[allowed.length - 1];
  // Walk fine→coarse; the FIRST (finest) bucket within budget wins.
  for (const b of allowed) {
    const count = Math.ceil(Math.max(spanMs, 60_000) / bucketWidthMs(b));
    if (count <= maxBars) {
      pick = b;
      break;
    }
  }
  return pick;
}
